import { useRef, useState } from 'react';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { Loader2, Scissors } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useQuotaUpsellDialog } from '@/hooks/use-quota-upsell-dialog';
import {
  EDIT_ONLY_MIN_SECONDS,
  adjustEditOnlySelection,
  defaultEditOnlySelection,
  formatWindowTime,
} from '@/lib/projects/editOnly';
import { START_EDIT_ONLY_LIBRARY, START_EDIT_ONLY_PROJECT } from '@/queries/edit-only';
import type {
  StartEditOnlyLibraryMutation,
  StartEditOnlyLibraryMutationVariables,
  StartEditOnlyProjectMutation,
  StartEditOnlyProjectMutationVariables,
} from '@/generated/edit-only';

interface EditOnlyRescueProps {
  kind: 'project' | 'library';
  id: string;
  videoUrl?: string | null;
  durationSeconds: number;
  // Start of a ranged project/library in the source video (0 otherwise).
  rangeStart?: number;
  onConverted?: () => void;
}

// Shown under the failure message when the video has no speech: the user picks
// a segment of up to one minute and continues in the editor with that clip.
export function EditOnlyRescue({
  kind, id, videoUrl, durationSeconds, rangeStart = 0, onConverted,
}: EditOnlyRescueProps) {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const offset = rangeStart > 0 ? rangeStart : 0;
  const [selection, setSelection] = useState(() => defaultEditOnlySelection(durationSeconds, offset));
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { dialog: quotaDialog, handleQuotaError } = useQuotaUpsellDialog();
  const [startEditOnly, { loading }] = useMutation<
    StartEditOnlyLibraryMutation | StartEditOnlyProjectMutation,
    StartEditOnlyLibraryMutationVariables | StartEditOnlyProjectMutationVariables
  >(kind === 'project' ? START_EDIT_ONLY_PROJECT : START_EDIT_ONLY_LIBRARY);

  const canChooseSegment = durationSeconds > EDIT_ONLY_MIN_SECONDS;

  const chooseSegment = (next: number[]) => {
    const adjusted = adjustEditOnlySelection(selection, [next[0], next[1]], { durationSeconds, rangeStart: offset });
    setSelection(adjusted);
    // Show the frame at the handle that moved.
    const movedTime = next[0] !== selection.start ? adjusted.start : adjusted.end;
    if (videoRef.current) videoRef.current.currentTime = movedTime;
  };

  const convert = async () => {
    try {
      await startEditOnly({ variables: { id, startSeconds: selection.start, endSeconds: selection.end } });
      onConverted?.();
    } catch (error) {
      if (!handleQuotaError(error)) setErrorMessage(t('editOnly.error'));
    }
  };

  return (
    <div className="w-full max-w-xl space-y-4 rounded-lg border border-border bg-card p-4 text-left">
      <div className="space-y-1">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-foreground">
          <Scissors className="h-4 w-4" />
          {t('editOnly.title')}
        </h2>
        <p className="text-sm text-muted-foreground">{t('editOnly.description')}</p>
      </div>

      {videoUrl && (
        <video
          ref={videoRef}
          src={videoUrl}
          controls
          preload="metadata"
          className="aspect-video w-full rounded-md bg-black"
        />
      )}

      {canChooseSegment && (
        <Slider
          value={[selection.start, selection.end]}
          min={offset}
          max={offset + durationSeconds}
          step={1}
          minStepsBetweenThumbs={EDIT_ONLY_MIN_SECONDS}
          onValueChange={chooseSegment}
          thumbLabels={[t('editOnly.startLabel'), t('editOnly.endLabel')]}
        />
      )}

      <p className="text-sm text-foreground">
        {t('editOnly.window', {
          start: formatWindowTime(selection.start),
          end: formatWindowTime(selection.end),
          seconds: Math.round(selection.end - selection.start),
        })}
      </p>

      {kind === 'project' && (
        <p className="text-xs text-muted-foreground">
          {t('editOnly.projectCharge', { duration: formatWindowTime(durationSeconds) })}
        </p>
      )}

      <Button onClick={convert} disabled={loading} className="w-full">
        {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        {t('editOnly.useSegment')}
      </Button>

      <Dialog open={!!errorMessage} onOpenChange={() => setErrorMessage(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('editOnly.errorTitle')}</DialogTitle>
            <DialogDescription>{errorMessage}</DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
      {quotaDialog}
    </div>
  );
}
